import { useState, useEffect, useRef } from 'react';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate, NavLink, useParams, useLocation } from 'react-router-dom';
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
// @mui
import { styled } from '@mui/material/styles';
import { Button, Typography, Container, Box, Modal } from '@mui/material';
import config from "../../config";
import { encryptData } from '../../utils/cryptoUtils';

import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle';
import "bootstrap-icons/font/bootstrap-icons.css";
import '../common.scss';

import * as userActions from "../../actions/userActions";
import AccessDenied from '../common/AccessDenied';

import * as leadBankStatementActions from "../../actions/leadBankStatementActions";
import * as leadActions from "../../actions/leadActions";

// ----------------------------------------------------------------------

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------


const UploadBankStatement = (props) => {
  const { leadId, id } = useParams();
  const location = useLocation();
  const { actions, usersData, fetchLeadIdBankStatement, allLeadBankStatementByLeadData } = props;
  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm();
  const navigate = useNavigate();

  const [addModulePermissionStatus, setAddModulePermissionStatus] = useState(false);
  const [editModulePermissionStatus, setEditModulePermissionStatus] = useState(false);
  const [userHasPermission, setUserHasPermission] = useState(true);
  const [permissions, setPermissions] = useState([]);

  const [allUsers, setAllUsers] = useState([]);
  const [selectedBankStatementFiles, setSelectedBankStatementFiles] = useState([]);
  const [getLeadId, setLeadId] = useState('');
  const [leadBankStatementId, setLeadBankStatementId] = useState('');
  const [getAllLeadBankStatementByLeadData, setAllLeadBankStatementByLeadData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const formRef = useRef(null);

  useEffect(() => {
    // check user is logged-in or not
    const authToken = localStorage.getItem('authToken');
    if (authToken === null) {
      navigate('/login');
    }

    const obj = { 'start': '0', 'length': config.MAX_RECORDS };
    const requestData = encryptData(obj);
    actions.usersData(requestData);
    if (usersData.success) {
      setAllUsers(usersData.data);
    }
  }, []);

  const handleBankStatementFileChange = (e) => {
    const files = Array.from(e.target.files);
    const base64Files = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target.result;
        const fileName = file.name;
        base64Files.push({ file: base64Data, name: fileName });
        if (base64Files.length === files.length) {
          setSelectedBankStatementFiles(base64Files);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const onSubmit = (data) => {

    const leadId = props.leadId;
    setLeadId(leadId);

    if (!leadBankStatementId && (!selectedBankStatementFiles || selectedBankStatementFiles.length === 0)) {
      toast.error("No file has been selected.", {
        position: "top-right",
      });
    } else if (data.bank_statement_note.trim() === "") {
      toast.error("Note is blank.", {
        position: "top-right",
      });
    } else {
      const params = { "lead_id": leadId, "bank_statement_note": data.bank_statement_note, "bank_statement_file": selectedBankStatementFiles };

      if (leadBankStatementId !== '') {
        params.lead_bank_statement_id = leadBankStatementId;
      }

      const requestData = encryptData(params);
      const response = actions.addEditLeadBankStatement(requestData, navigate);

      response.then((result) => {
        // console.log("Promise resolved - add/edit lead form:", result); 
        if (result.data.success === 1) {
          toast.success(result.data.message, {
            position: "top-right",
          });
          const leadBankStatementId = result.data.lead_bank_statement_id;
          // setLeadBankStatementId(leadBankStatementId);
          fetchLeadIdBankStatement();
          reset();
          setSelectedBankStatementFiles("");
          props.updateParentState(false);
        } else {
          toast.error(result.data.message, {
            position: "top-right",
          });
        }
      }).catch((error) => {
        toast.error(error, {
          position: "top-right",
        });
      });
    }
  };

  // console.log("allLeadBankStatementByLeadData =", allLeadBankStatementByLeadData);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const filteredItems = (allLeadBankStatementByLeadData ?? []).filter(item =>
    item.formatetdID.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.assignedUser.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.statusName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const startIndex = (currentPage - 1) * config.ITEMS_PER_PAGE;
  const endIndex = startIndex + config.ITEMS_PER_PAGE;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredItems.length / config.ITEMS_PER_PAGE);

  const docViewUrl = `${config.API_BASE_URL}uploads/lead_bank_statement_document/`;

  const onRowClickAction = (itemObj, btnAction) => {
    if (btnAction === 'edit') {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth' });
      }

      setLeadBankStatementId(itemObj.bank_statement_id);
      setValue("bank_statement_file", docViewUrl + itemObj.bank_statement_file);      
      setValue("bank_statement_note", itemObj.bank_statement_note);

    } else if (btnAction === "delete") {
      const leadId = props.leadId;
      const obj = { "lead_id": leadId, "id": itemObj.bank_statement_id, "basic_info_type": "bank_statement" };
      const requestData = encryptData(obj);
      const response = actions.deleteLeadBasicInfo(requestData);
      response.then((result) => {
        // console.log("Promise resolved - deleteLeadBasicInfo result:", result);
        if (result.data.success === 1) {
          toast.success(result.data.data.message, {
            position: "top-right",
          });

          fetchLeadIdBankStatement();
          props.updateParentState(false);
  
        } else {
          toast.error(result.data.data.message, {
            position: "top-right",
          });
        }
      }).catch((error) => {
        toast.error(error, {
          position: "top-right",
        });
      });
    }
  }

  const handleNewButton = () => {
    setLeadBankStatementId("");
    setSelectedBankStatementFiles("");
    reset();
  }

  return (
    <>
      <h5 className="fw-bold">Upload Bank Statement</h5>

      <div id="main-wrap" className='mt-4'>
        <div className="table-wrap">
          <table className="table table-hover">
            <thead>
              <tr>
                {/* <th>Bank Statement ID</th> */}
                <th>Bank Statement UID</th>
                <th>Created Date</th>
                <th>View File</th>
                {!props.showActionLeadInfo && (
                  <th>Action</th>
                )}
              </tr>
            </thead>

            {paginatedItems.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center' }}>
                    <h5>No Bank Statement</h5>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {paginatedItems.map((item, index) => (
                  <tr key={index}>
                    {/* <td>{item.formatetdID}</td> */}
                    <td>{item.uid}</td>
                    <td>{item.created_date}</td>
                    <td>
                      <span className="badge text-bg-success text-white">
                        <a target="new" href={docViewUrl + item.bank_statement_file} className='text-success text-decoration-none'>
                          <i className="bi bi-eye">&nbsp;</i>
                        </a>
                      </span>
                    </td>
                    {!props.showActionLeadInfo && (
                      <td>
                        <div className="btn-group dropstart">
                          <button type="button" className="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="bi bi-three-dots-vertical">&nbsp;</i>
                          </button>
                          <ul className="dropdown-menu">
                            <li>
                              <RouterLink onClick={() => onRowClickAction(item, 'edit')} ><i className="bi bi-pen">&nbsp;</i>Edit</RouterLink>
                            </li>
                            <li>
                              <RouterLink onClick={() => onRowClickAction(item, 'delete')} ><i className="bi bi-trash">&nbsp;</i>Delete</RouterLink>
                            </li>
                          </ul>
                        </div>
                      </td>
                    )}
                    

                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        <div>
          <nav className='mb-1'>
            <ul className="pagination pagination-md justify-content-end">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                <li className="page-item">
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    disabled={currentPage === page}
                    className="page-link"
                  >
                    {page}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      <div id="main-wrap" className='mt-4'>
        <div className='row justify-content-between'>

          <div className="row">
            <div className='col-md-9'>
              <h5> {leadBankStatementId === '' ? 'Add' : "Edit"} Bank Statement</h5>
            </div>
            {leadBankStatementId !== '' && (
              <div className='col-md-3'>
                <div className='form-group text-end mb-2'>
                  <button className="btn btn-primary" onClick={() => handleNewButton()}>Add New</button>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data" ref={formRef}>
            <div className="row" id="bank-statement-1">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="bank_statement_file">
                    <span>File Upload <a style={{ color: "red" }}>*</a></span>
                    <input disabled={props.leadStatusFlag === true} type='file' id='bank_statement_file' name='bank_statement_file' className='form-control' onChange={handleBankStatementFileChange} />
                  </label>
                </div>
              </div>
              <div className='col-md-12'>
                <div className='form-group'>
                  <label htmlFor="bank_statement_note">
                    <span>Notes <a style={{ color: "red" }}>*</a></span>
                    <textarea readOnly={props.leadStatusFlag === true}
                      id='bank_statement_note'
                      className='form-control'
                      placeholder="Type Something" {...register('bank_statement_note')}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="form-group mb-0">
                  <button disabled={props.leadStatusFlag === true} className="btn btn-primary">Submit</button>
                </div>
              </div>
            </div>

          </form>
        </div>
      </div>
    </>
  );
}


function mapStateToProps(state) {
  return {
    usersData: state.usersData,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign(userActions.default, leadBankStatementActions.default, leadActions.default), dispatch),
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(UploadBankStatement);
